import { useLayoutEffect, useRef, type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { useShellChrome, useShellSlot } from "./ShellProvider";
import { useHasOverlay } from "./useShellDismiss";
import type { ShellRegion } from "./shellChrome";

// Generates no box, so claimed content lands in the shell's own layout rather than in a wrapper.
const SlotAnchor = styled("div")({ display: "contents" });

interface ShellSlotHostProps {
  slot: ShellRegion;
  /** Unmounts the host, fallback and claimed content alike, while an overlay is open, unless the
   *  owning claim is marked `visibleDuringOverlay`. */
  hiddenDuringOverlay?: boolean;
  /** Rendered while nothing claims the slot. */
  children?: ReactNode;
}

export function ShellSlotHost({ slot, hiddenDuringOverlay = false, children }: ShellSlotHostProps) {
  const chrome = useShellChrome();
  const { ownerId, visibleDuringOverlay } = useShellSlot(chrome, slot);
  const hasOverlay = useHasOverlay();
  const hidden = hiddenDuringOverlay && hasOverlay && !visibleDuringOverlay;
  const anchorRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (hidden) return undefined;
    return chrome.mountHost(slot, anchorRef.current);
  }, [chrome, slot, hidden]);

  if (hidden) return null;

  return (
    <>
      {ownerId === null && children}
      <SlotAnchor ref={anchorRef} />
    </>
  );
}
